import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { useServerTable } from "@/hooks/useServerTable";
import { observer } from "mobx-react-lite";
import { useBackofficeUserColumns } from "./Columns";
import { List } from "@/components/List";
import { backoffice } from "@/endpoints/dryp/backoffice";
import { getTableSearchParam } from "@/utils/filters";
import { CUSTOM_FILTERS } from "@/config/list";
import { OrganizationsFilter, RolesFilter } from "@/components/filters/BackofficeUserFilters";
import { useCallback } from "react";

export const BackofficeUsers = observer(() => {
	const usersApi = useAsyncStateStore(backoffice.getUsers, { debounce: 300 });

	const { table, refresh } = useServerTable({
		columns: useBackofficeUserColumns(() => refresh()),
		data: usersApi.data?.items || [],
		fetchFn: params => {
			const search = getTableSearchParam(params?.filters);
			const organizationIds = params?.filters?.find(f => f.id === CUSTOM_FILTERS.organizationIds)?.value as
				| string[]
				| undefined;
			const roleNames = params?.filters?.find(f => f.id === CUSTOM_FILTERS.roleNames)?.value as
				| string[]
				| undefined;

			return usersApi.fetch(
				{ pageNumber: params.page, pageSize: params.pageSize },
				params.sort,
				search,
				organizationIds,
				roleNames,
			);
		},
		initialValues: {
			page: 0,
			pageSize: 10,
			columnVisibility: { id: false },
		},
		enableUrlParams: true,
		initFromUrl: true,
	});

	const getSelectedFilter = (filterKey: string) =>
		(table.getState().columnFilters.find(f => f.id === filterKey)?.value as string[]) || [];

	const updateFilter = useCallback(
		(filterKey: string, values: string[]) => {
			const otherFilters = table.getState().columnFilters.filter(f => f.id !== filterKey);
			table.setColumnFilters(
				values.length > 0 ? [...otherFilters, { id: filterKey, value: values }] : otherFilters,
			);
		},
		[table],
	);

	return (
		<div className="flex flex-col gap-4 md:gap-6 p-4 md:p-6">
			<header className="flex items-center justify-between">
				<h1 className="text-2xl md:text-4xl font-semibold tracking-tight">Users</h1>
			</header>

			<List
				table={table}
				toolbar={{
					filterKey: CUSTOM_FILTERS.search,
					customFilter: (
						<div className="flex items-center gap-2">
							<OrganizationsFilter
								selected={getSelectedFilter(CUSTOM_FILTERS.organizationIds)}
								onChange={values => updateFilter(CUSTOM_FILTERS.organizationIds, values)}
							/>
							<RolesFilter
								selected={getSelectedFilter(CUSTOM_FILTERS.roleNames)}
								onChange={values => updateFilter(CUSTOM_FILTERS.roleNames, values)}
							/>
						</div>
					),
				}}
				layout={{
					loading: usersApi.loading,
				}}
				pagination={{
					total: usersApi.data?.query?.pagination.totalItems || 0,
				}}
			/>
		</div>
	);
});
